import { flatten, throttle } from 'lodash';
import * as React from 'react';
import { mac } from 'js/browser/browser_detection';
import { KeyboardBindingContext } from 'js/file_viewer/keyboard/context';
import { KeyboardBindingMap } from 'js/file_viewer/keyboard/keyboard_binding_map';
/**
 * Returns true if we want to allow a keyboard event's callback to fire.
 * Filter key events that target nodes we don't want to intercept.
 */
function filterKeyboardEvent(event) {
    const target = event.target || event.srcElement;
    if (!target) {
        return true;
    }
    // It seems like the = '' default assignment below shouldn't be necessary, based on how event.target
    // is typed, but tests fail without it.
    const tagName = target.tagName;
    const isInputField = tagName === 'INPUT' || tagName === 'SELECT' || tagName === 'TEXTAREA';
    const isButton = target instanceof HTMLInputElement && (target.type === 'submit' || target.type === 'button');
    const isMenu = tagName === 'NAV' || target.role === 'menu' || target.role === 'menuitem';
    // We treat "contenteditable" text-fields as inputs
    const isContentEditable = target.hasAttribute &&
        target.hasAttribute('contenteditable') &&
        target.getAttribute('contenteditable').toLowerCase() !== 'false';
    return !isContentEditable && (!isInputField || isButton) && !isMenu;
}
const DEFAULT_SCOPE = 'default';
const flattenBindings = (bindings) => {
    return flatten(Object.values(bindings));
};
/**
 * This component provides functionality over context for the KeyboardBindingConnector to
 * map global key events with callback functions.
 */
export const KeyboardBindingProvider = (props) => {
    const { disableBindings, children } = props;
    const osModifierKey = mac ? 'metaKey' : 'ctrlKey';
    // These are the bindings as they are provided to the components.
    // We store these in an array and later convert them into a map for easy lookup.
    const registeredBindings = React.useRef({});
    const currentScope = React.useRef(DEFAULT_SCOPE);
    // This is a cached map of keyboardBindings we're currently using.
    // It is created with generateActiveKeyboardBindings.
    const bindingMap = React.useRef(null);
    const updateKeyBindings = React.useCallback((bindings, name, scope) => {
        var _a;
        if (!registeredBindings.current[scope]) {
            registeredBindings.current[scope] = {};
        }
        if (bindings) {
            if (registeredBindings.current[scope][name]) {
                throw new Error(`Keybindings for name ${name} registered twice`);
            }
            registeredBindings.current[scope][name] = bindings;
        }
        else {
            delete registeredBindings.current[scope][name];
        }
        const currentBindings = (_a = registeredBindings.current[currentScope.current]) !== null && _a !== void 0 ? _a : {};
        bindingMap.current = new KeyboardBindingMap(flattenBindings(currentBindings), osModifierKey);
    }, [osModifierKey]);
    /**
     * A KeyboardBinding[] is given to the KeyboardBindingConnector as a prop.
     * This function is called on the mount of the KeyboardBindingConnector.
     * The keyboard register will store its registrationIdentifier and use it when it unmounts
     * to unregister any callbacks.
     */
    const registerKeyBindings = React.useCallback((bindings, name, useScope) => {
        if (useScope && name !== currentScope.current) {
            currentScope.current = name;
        }
        updateKeyBindings(bindings, name, useScope ? name : DEFAULT_SCOPE);
    }, [updateKeyBindings]);
    /**
     * This unregisters any keyboard bindings set by a KeyboardBindingConnector
     * when it unmounts.
     */
    const unregisterKeyBindings = React.useCallback((name, useScope) => {
        if (useScope && currentScope.current === name) {
            currentScope.current = DEFAULT_SCOPE;
        }
        updateKeyBindings(undefined, name, useScope ? name : DEFAULT_SCOPE);
    }, [updateKeyBindings]);
    const onKeyDown = React.useCallback((e) => {
        var _a;
        // Only call callbacks for events that pass the filter.
        if (!filterKeyboardEvent(e)) {
            return;
        }
        const callback = (_a = bindingMap.current) === null || _a === void 0 ? void 0 : _a.getCallbackForEvent(e);
        if (!callback) {
            return;
        }
        callback(e);
    }, []);
    React.useEffect(() => {
        if (disableBindings !== true) {
            const throttled = throttle(onKeyDown, 0);
            window.addEventListener('keydown', throttled);
            return () => {
                window.removeEventListener('keydown', throttled);
            };
        }
        return;
    }, [disableBindings, onKeyDown]);
    const contextValue = React.useMemo(() => ({
        registerKeyBindings: registerKeyBindings,
        unregisterKeyBindings: unregisterKeyBindings,
        updateKeyBindings: updateKeyBindings,
    }), [registerKeyBindings, unregisterKeyBindings, updateKeyBindings]);
    return (React.createElement(KeyboardBindingContext.Provider, { value: contextValue }, children));
};
KeyboardBindingProvider.displayName = "KeyboardBindingProvider"
//# sourceMappingURL=keyboard_provider.preenvesnextjs.map
